// TypeScript-first Organization Loader Hook - No runtime validation needed

import { useState, useEffect, useCallback } from "react";

import { OrganizationEvents } from "@generated/organization";
import { EventBus } from "src/platform/events/EventBus";
import { useRegistryCache } from "src/platform/hooks/useRegistryCache";

import organizationApi from "../api/organizationApi";
import { Organization } from "../types";

export interface UseOrganizationLoaderReturn {
  data: Organization | null;
  loading: boolean;
  error: string | null;
  loadOrganization: (alias: string) => Promise<void>;
  refreshOrganization: () => Promise<void>;
}

export function useOrganizationLoader(
  eventBus: EventBus,
): UseOrganizationLoaderReturn {
  const [data, setData] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentAlias, setCurrentAlias] = useState<string | null>(null);

  // Get or register the organization module bus
  const moduleBus =
    eventBus.getModuleBus("organization") ||
    eventBus.registerModule("organization");

  const cache = useRegistryCache("organization");

  const loadOrganization = useCallback(
    async (alias: string) => {
      setLoading(true);
      setError(null);
      setCurrentAlias(alias);

      try {
        // Emit event for tracking
        moduleBus.emit(OrganizationEvents.LOAD_ORGANIZATION, { alias });

        const organization = await cache.get(
          `currentOrganization:${alias}`,
          () => organizationApi.getOrganization(alias),
        );
        setData(organization);

        // Emit success event
        moduleBus.emit(OrganizationEvents.ORGANIZATION_LOADED, {
          organization,
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load organization";
        setError(errorMessage);

        // Emit error event
        moduleBus.emit(OrganizationEvents.ORGANIZATION_ERROR, {
          alias,
          error: errorMessage,
        });
      } finally {
        setLoading(false);
      }
    },
    [moduleBus],
  );

  const refreshOrganization = useCallback(async () => {
    if (currentAlias) {
      await loadOrganization(currentAlias);
    }
  }, [currentAlias, loadOrganization]);

  // Listen for organization updates from other components
  useEffect(() => {
    const handleOrganizationUpdated = (payload: unknown) => {
      const organization = (payload as { organization: Organization })
        .organization;
      if (data?.alias === organization.alias) {
        setData(organization);
      }
    };

    const handleOrganizationDeleted = (payload: unknown) => {
      const alias = (payload as { alias: string }).alias;
      if (data?.alias === alias) {
        setData(null);
        setCurrentAlias(null);
      }
    };

    const updateSubscription = moduleBus.subscribe(
      OrganizationEvents.ORGANIZATION_UPDATED,
      handleOrganizationUpdated,
    );
    const deleteSubscription = moduleBus.subscribe(
      OrganizationEvents.ORGANIZATION_DELETED,
      handleOrganizationDeleted,
    );

    return () => {
      moduleBus.unsubscribe(updateSubscription);
      moduleBus.unsubscribe(deleteSubscription);
    };
  }, [moduleBus, data]);

  return {
    data,
    loading,
    error,
    loadOrganization,
    refreshOrganization,
  };
}
